import { useAccount, useDashboardContext, useToasts } from '@src/contexts';
import useToggle from '@src/hooks/utils/useToggle';
import {
  useAddPaymentLink,
  useGetPrimarySettlementAccounts,
  useUpdatePaymentLink
} from '@src/hooks/queries/user';
import { Path } from '@src/navigations/routes';
import ClientApi from '@src/types/client';
import { getCurrencySymbol } from '@src/utils/getCurrencySymbol';
import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PaymentLinkFormI } from '../components/singlePaymentLinkForm';
import { getPaymentLinkValidationSchema } from '../utils/getPaymentLinkValidationSchema';

interface UsePaymentLinkFormOptions {
  paymentLinkObj?: ClientApi.General.PaymentLinkI;
  handleClose: () => void;
  shouldRedirect?: boolean;
  variant?: 'receiver' | 'raas';
}

export interface UsePaymentLinkFormResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema: any;
  initialValues: PaymentLinkFormI;
  handleSubmit: (values: PaymentLinkFormI) => void;
  buttonLabel: string;
  title: string;
  isLoading: boolean;
  primarySettlementAccount: ClientApi.General.SettlementAccountI | undefined;
  primarySettlementAccountLoading: boolean;
  primarySettlementAccountError: boolean;
  primarySettlementAccountRefetch: () => void;
  shouldDisableForm: boolean;
  showVeriffModal: boolean;
  handleVeriffModalClose: () => void;
}

const usePaymentLinkForm = ({
  handleClose,
  paymentLinkObj,
  shouldRedirect,
  variant = 'receiver'
}: UsePaymentLinkFormOptions): UsePaymentLinkFormResponse => {
  const {
    state: showVeriffModal,
    handleFalse: handleVeriffModalClose,
    handleTrue: handleVeriffModalOpen
  } = useToggle();

  const { addToast } = useToasts();
  const queryClient = useQueryClient();
  const { userAddress, isRemittanceBusiness } = useAccount();
  const { shouldBusinessMakeTransactions } = useDashboardContext();
  const navigate = useNavigate();

  const isEdit = !!paymentLinkObj;
  const shouldDisableForm = isEdit && paymentLinkObj?.status !== 'Active';
  const accountCurrency = getCurrencySymbol(userAddress?.country);

  const {
    data: primarySettlementAccount,
    isLoading: primarySettlementAccountLoading,
    isError: primarySettlementAccountError,
    refetch: primarySettlementAccountRefetch
  } = useGetPrimarySettlementAccounts({
    enabled: variant === 'receiver'
  });

  const initialValues = useMemo<PaymentLinkFormI>(() => {
    return {
      amount: paymentLinkObj?.amount?.toString() || '',
      description: paymentLinkObj?.description || '',
      linkName: paymentLinkObj?.name || '',
      targetCurrency:
        variant === 'raas'
          ? 'USD'
          : paymentLinkObj?.targetCurrencyCode ||
            primarySettlementAccount?.accountCurrencyCode ||
            accountCurrency,
      redirectUrl: paymentLinkObj?.redirectUrl || ''
    };
  }, [paymentLinkObj, primarySettlementAccount, variant]);

  const { mutate: addPaymentLinkMutate, isLoading: addSettlementLoading } = useAddPaymentLink({
    onSuccess: () => {
      handleSuccessCallback('add');
    }
  });

  const { mutate: updatePaymentLinkMutate, isLoading: updatePaymentLinkLoading } =
    useUpdatePaymentLink({
      onSuccess: () => {
        handleSuccessCallback('update');
      }
    });

  const handleSuccessCallback = (type: 'add' | 'update') => {
    handleClose();

    if (type === 'add') {
      addToast({ body: 'Payment link added', title: 'Success' });
    }
    if (type === 'update') {
      addToast({ body: 'Payment link updated', title: 'Success' });
    }

    queryClient.invalidateQueries(['getPaymentLinks']);
    queryClient.invalidateQueries(['getPaymentLinkDetails']);
    queryClient.invalidateQueries(['getPaymentLinkSummary']);

    if (shouldRedirect) {
      if (isRemittanceBusiness) {
        navigate(Path.SenderPaymentLinks);
      } else {
        navigate(Path.ReceiverPaymentLinks);
      }
    }
  };

  const handleAdd = (values: PaymentLinkFormI) => {
    addPaymentLinkMutate({
      name: values.linkName,
      targetCurrencyCode: values.targetCurrency,
      amount: isNaN(Number(values.amount)) ? 0 : Number(values.amount),
      description: values.description,
      redirectUrl: values.redirectUrl,
      extraFields: [],
      frequency: 1
    });
  };

  const handleEdit = (values: PaymentLinkFormI) => {
    updatePaymentLinkMutate({
      reference: paymentLinkObj?.reference || '',
      name: values.linkName,
      targetCurrencyCode: values.targetCurrency,
      amount: isNaN(Number(values.amount)) ? 0 : Number(values.amount),
      description: values.description,
      redirectUrl: values.redirectUrl,
      extraFields: [],
      frequency: 1
    });
  };

  const handleSubmit = (values: PaymentLinkFormI) => {
    if (!shouldBusinessMakeTransactions) {
      handleVeriffModalOpen();
      return;
    }

    if (isEdit) {
      if (!shouldDisableForm) {
        handleEdit(values);
      }
    } else {
      handleAdd(values);
    }
  };

  const validationSchema = getPaymentLinkValidationSchema();

  const buttonLabel = isEdit ? 'Update payment link' : 'Create payment link';
  const title = isEdit ? 'Edit payment link' : 'Create new payment link';
  const isLoading = addSettlementLoading || updatePaymentLinkLoading;

  return {
    validationSchema,
    initialValues,
    handleSubmit,
    buttonLabel,
    title,
    isLoading,
    primarySettlementAccount,
    primarySettlementAccountError,
    primarySettlementAccountLoading,
    primarySettlementAccountRefetch,
    shouldDisableForm,
    showVeriffModal,
    handleVeriffModalClose
  };
};

export default usePaymentLinkForm;
